import { DEFAULT_PRINTOUT_COLUMNS } from "../../pages/DaySchedule/Header/usePrintout";
import { isInPTO } from "../../utils/dateUtils";
import { DailyPrintoutGroupingOption } from "../../utils/models";
import { groupRows } from "./group/sectionGrouping";
import { resolveActiveCustomTimeGroups } from "./layout/customTimeGroups";
import { planLayout } from "./phases/planLayout";
import { renderSheets } from "./phases/renderSheets";
import { assembleSourceRows } from "./phases/sourceRows";
import { splitSheets } from "./sheets/splitMode";
import { sortRows } from "./sort/rowSort";
import { selectSortSource } from "./sort/sourceSelector";
import { createDayViewWithoutFloors } from "./summary/createDayViewWithoutFloors";
import type { CreateDetailedDayViewArgs, NurseInShiftWithPto, PipelineContext, RenderSettings } from "./types";

/**
 * The public entry point of the daily-printout pipeline.
 *
 * Drop-in replacement for today's `downloadDetailedDayView.tsx`'s `createDetailedDayView`.
 * Shape and side-effect contract preserved: mutates `args.workbook` in place, returns `void`.
 *
 * Pipeline:
 * 1. **Source rows** — assemble `ScheduleRow[]` from staff shifts + gap placeholders
 * 2. **Sort** — pick the sort source (configured / fallback) and sort rows
 * 3. **Group** — partition rows into ordered sections (multi-level supported)
 * 4. **Sheet split** — partition sections into one or more sheets (incl. custom tabs)
 * 5. **Plan layout** — pick standard / pivot per sheet
 * 6. **Render** — emit each sheet to the workbook
 *
 * The `summary` flag short-circuits to a separate, much simpler renderer (mapping §1 mode branch).
 */
export const createDetailedDayView = async (args: CreateDetailedDayViewArgs): Promise<void> => {
	if (args.summary) {
		return createDayViewWithoutFloors({
			workbook: args.workbook,
			hospitalData: args.hospitalData,
			weekIndex: args.weekIndex,
			dayIndex: args.dayIndex,
			department: args.department,
			day: args.day,
			date: args.date,
			printNotes: args.printNotes,
			printoutGroupBy: args.printoutGroupBy,
			showDhppd: args.showDhppd,
			showNhppd: args.showNhppd,
			showActualStaffColumn: args.showActualStaffColumn,
			showActualHoursColumn: args.showActualHoursColumn,
			dhppd: args.dhppd,
			nhppd: args.nhppd,
			dayViewCensus: args.dayStats.census,
			dayViewNursingHours: args.dayStats.totalHours,
			dayViewHppd: args.dayStats.hppd,
			jobManagementEnabled: args.jobManagementEnabled,
			censusPointInTimes: args.censusPointInTimes,
		});
	}

	const settings: RenderSettings = {
		enabledColumns: (args.columns ?? DEFAULT_PRINTOUT_COLUMNS).filter((col) => col.enabled),
		rowGroupingBy: args.rowGroupingBy ?? [],
		columnGroupingBy: args.columnGroupingBy ?? null,
		printoutGroupBy: args.printoutGroupBy,
		separateSheetsBy: args.separateSheetsBy ?? DailyPrintoutGroupingOption.NONE,
		customSheetTabs: args.customSheetTabs,
		showAdmins: args.showAdmins,
		showLocationCensus: args.showLocationCensus ?? false,
		printNotes: args.printNotes,
		showRoleGroupStats: args.showRoleGroupStats,
		staffPerRow: args.staffPerRow ?? 1,
		spaceBetweenGroups: args.spaceBetweenGroups ?? true,
		hideHppdNursingHours: args.hideHppdNursingHours ?? false,
		flagNotOnboarded: args.flagNotOnboarded,
		flagAgency: args.flagAgency,
		divideGroupsByPage: args.divideGroupsByPage ?? false,
		orientation: args.orientation,
		fitToPage: args.fitToPage ?? true,
		fitToWidth: args.fitToWidth ?? 1,
		fitToHeight: args.fitToHeight ?? 0,
		printoutColumnWidths: args.printoutColumnWidths,
		mergeSimilarShiftTimes: args.mergeSimilarShiftTimes,
		alignGroupTimesToPopular: args.alignGroupTimesToPopular,
		customTimeGroups: args.customTimeGroups,
		appendRoleToName: args.appendRoleToName,
		hideColumnNames: args.hideColumnNames,
		appendEmptyColumns: args.appendEmptyColumns,
		customColumnNames: args.customColumnNames,
		includeTimeOffReason: args.includeTimeOffReason ?? false,
		sortBy: args.sortBy,
	};

	// Map PTO-deleted shifts into the renderer-friendly shape, optionally enriching notes from
	// the nurse's specific preferences when `includeTimeOffReason` is on (mapping §9.3).
	const ptoShifts: NurseInShiftWithPto[] = (args.ptoDeletedShifts ?? []).map((shift) => {
		let notes = "";
		if (settings.includeTimeOffReason) {
			const fullNurse = args.hospitalData.nurses.find((n) => n.id === shift.nurseId);
			const matchingPref = fullNurse?.employeeSpecificPreferences.find((pref) => isInPTO(pref, shift.startTime, args.department.dayStart, shift.endTime));
			notes = matchingPref?.notes ?? "";
		}
		return { ...shift, isPtoShift: false, notes };
	});

	// Phase 1 — assemble source rows
	const { rows, departmentLocationsRequired, dayAbbr, censusByLocationName } = assembleSourceRows({
		hospitalData: args.hospitalData,
		weekIndex: args.weekIndex,
		dayIndex: args.dayIndex,
		department: args.department,
		date: args.date,
		staffingNurses: args.nurses,
		ptoShifts,
		selectedLocations: args.selectedLocations,
		availableLocations: args.availableLocations,
		showLocationCensus: settings.showLocationCensus,
		timezone: args.department.timezone,
		dayStart: args.department.dayStart,
		printGaps: args.printGaps,
	});

	const context: PipelineContext = {
		hospitalData: args.hospitalData,
		department: args.department,
		date: args.date,
		timezone: args.department.timezone,
		jobManagementEnabled: args.jobManagementEnabled,
		lastNameFirst: args.lastNameFirst,
		customStringOrderMap: args.customStringOrderMap,
		shiftTimeAnchors: args.shiftTimeAnchors,
		dayStats: args.dayStats,
		censusPointInTimes: args.censusPointInTimes,
		departmentLocationsRequired,
		dayAbbr,
		censusByLocationName,
		customTimeGroups: resolveActiveCustomTimeGroups({
			config: settings.customTimeGroups,
			timezone: args.department.timezone,
			dayStart: args.department.dayStart,
			otherLabel: args.customTimeGroupsOtherLabel,
		}),
		title: `${args.department.display_name} - DAILY SCHEDULE`,
	};

	// Phase 2 — sort
	const sortState = selectSortSource({ sortBy: args.sortBy, userId: args.userId });
	const sortedRows = sortRows({
		rows,
		sortState,
		customStringOrderMap: context.customStringOrderMap,
		jobManagementEnabled: context.jobManagementEnabled,
		department: context.department,
	});

	// Phase 3 — group
	const sections = groupRows({
		rows: sortedRows,
		printoutGroupBy: settings.printoutGroupBy,
		jobManagementEnabled: context.jobManagementEnabled,
		customStringOrderMap: context.customStringOrderMap,
		sortBy: args.sortBy,
		alignGroupTimesToPopular: settings.alignGroupTimesToPopular,
		shiftTimeAnchors: context.shiftTimeAnchors,
		day: context.date,
		timezone: context.timezone,
		customTimeGroups: context.customTimeGroups,
	});

	// Phase 4 — sheet split
	const sheetPlans = splitSheets({
		sections,
		separateSheetsBy: settings.separateSheetsBy,
		customSheetTabs: settings.customSheetTabs,
		department: context.department,
		jobManagementEnabled: context.jobManagementEnabled,
		alignGroupTimesToPopular: settings.alignGroupTimesToPopular,
		shiftTimeAnchors: context.shiftTimeAnchors,
		day: context.date,
		timezone: context.timezone,
		customTimeGroups: context.customTimeGroups,
		timeOffAndCallOuts: [...ptoShifts, ...args.callOutShifts],
	});

	// Phase 5 — plan layout
	const laidOutSheets = sheetPlans.map((sheet) =>
		planLayout({
			sheet,
			enabledColumns: settings.enabledColumns,
			staffPerRow: settings.staffPerRow,
			rowGroupingBy: settings.rowGroupingBy,
			columnGroupingBy: settings.columnGroupingBy,
			timezone: context.timezone,
			department: context.department,
			jobManagementEnabled: context.jobManagementEnabled,
			sortBy: args.sortBy,
			customStringOrderMap: context.customStringOrderMap,
			mergeSimilarShiftTimes: settings.mergeSimilarShiftTimes,
			customTimeGroups: settings.customTimeGroups,
			customTimeGroupsOtherLabel: args.customTimeGroupsOtherLabel,
		}),
	);

	// Phase 6 — render
	await renderSheets({
		workbook: args.workbook,
		sheets: laidOutSheets,
		ptoShifts,
		callOutShifts: args.callOutShifts,
		context,
		settings,
	});
};
